import React, { useState, useEffect } from "react";
import "./App.css";
import AccessControl from "./components/AccessControl";
import HomePage from "./components/HomePage";
import PassengerForm from "./components/PassengerForm";
import DriverForm from "./components/DriverForm";
import DriverDashboard from "./components/DriverDashboard";
import Notification from "./components/Notification";
import WaitingListView from "./components/WaitingListView";
import HistoryView from "./components/HistoryView";
import PasswordModal from "./components/PasswordModal";
import AdminView from "./components/AdminView";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import UpdateBanner from "./components/UpdateBanner";
import VersionChecker from "./components/VersionChecker";
import PendingRequestsNotifier from "./components/PendingRequestsNotifier";
import { setSpecialEventsHelpers } from "./utils/dateHelpers";
import { getSpecialEvents, getExcludedDates, listenToSpecialEvents, listenToExcludedDates } from "./services/specialEventsService";

function App() {
  const [currentView, setCurrentView] = useState("home");
  const [notification, setNotification] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Vérifier l'accès au chargement
  useEffect(() => {
    const access = localStorage.getItem("covoiturage_access") === "granted";
    setHasAccess(access);
  }, []);

  // Initialiser les helpers pour dateHelpers avec les listeners en temps réel
  useEffect(() => {
    setSpecialEventsHelpers(
      getSpecialEvents, 
      getExcludedDates,
      listenToSpecialEvents,
      listenToExcludedDates
    );
  }, []);

  const showNotification = (message, type = "info") => {
    setNotification({ type, message });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  const handleAccessGranted = () => {
    setHasAccess(true);
    showNotification(
      "Accès accordé ! Bienvenue dans votre espace covoiturage.",
      "success",
    );
  };

  const handleDriverFormSubmit = () => {
    showNotification(
      "Votre proposition a été enregistrée ! Vous pouvez maintenant voir les demandes.",
      "success",
    );
    setCurrentView("waiting-list");
  };

  const renderView = () => {
    switch (currentView) {
      case "passenger":
        return (
          <PassengerForm
            onSubmit={() => {}}
            onBack={() => setCurrentView("home")}
            showNotification={showNotification}
            onGoToWaitingList={() => setCurrentView("waiting-list")}
          />
        );
      case "driver":
        return (
          <DriverForm
            onBack={() => setCurrentView("home")}
            onSubmit={handleDriverFormSubmit}
          />
        );
      case "driver-dashboard":
        return (
          <DriverDashboard
            onBack={() => setCurrentView("home")}
            onGoToDriverForm={() => setCurrentView("driver")}
          />
        );
      case "waiting-list":
        return (
          <WaitingListView
            onBack={() => setCurrentView("home")}
            onGoToDriverForm={() => setCurrentView("driver")}
          />
        );
      case "history":
        return (
          <HistoryView
            onBack={() => setCurrentView("home")}
          />
        );
      case "admin":
        return (
          <AdminView
            onBack={() => setCurrentView("home")}
          />
        );

      default:
        return <HomePage onNavigate={setCurrentView} />;
    }
  };

  // Si pas d'accès, afficher le contrôle d'accès
  if (!hasAccess) {
    return (
      <ErrorBoundary>
        <div className="App">
          <AccessControl onAccessGranted={handleAccessGranted} />
        </div>
      </ErrorBoundary>
    );
  }

  // Si accès accordé, afficher l'application normale
  return (
    <ErrorBoundary>
      <div className="App">
        <header className="App-header">
          <h1>🚗 Covoiturage</h1>
          <div className="App-header-buttons">
            {/* Bouton pour accéder à la vue administrateur */}
            <button
              className="history-button"
              onClick={() => setShowPasswordModal(true)}
              title="Vue Administrateur"
            >
              🔧
            </button>
            {/* Bouton pour se déconnecter */}
            <button
              className="logout-button"
              onClick={() => {
                localStorage.removeItem("covoiturage_access");
                setHasAccess(false);
                setCurrentView("home");
              }}
              title="Se déconnecter"
            >
              🚪
            </button>
          </div>
        </header>

        <main className="App-main">{renderView()}</main>

        <Footer />

        {notification && (
          <Notification
            notification={notification}
            onClose={clearNotification}
          />
        )}

        {showPasswordModal && (
          <PasswordModal
            onClose={() => setShowPasswordModal(false)}
            onSuccess={() => {
              setShowPasswordModal(false);
              setCurrentView("admin");
            }}
          />
        )}

        <UpdateBanner />
        <VersionChecker />
        <PendingRequestsNotifier />
      </div>
    </ErrorBoundary>
  );
}

export default App;
